// ─────────────────────────────────────────────────────────────────
// objeler — Global singleton state for the WDoc Designer frontend
//
// Historically this was a 3-line empty object: `let objeler = {}`.
// The whole application reads/writes runtime properties onto it
// (User, Editor, App, Header, DocName, ModalFileParameters, ...).
//
// M2 multi-tenant additions:
//   - objeler.User now carries AccountId / AccountName / Role,
//     extracted from the JWT access token issued by the backend.
//   - Helper functions decodeJwt() and applyAccountContext() centralize
//     the (slightly fiddly) base64url JWT payload decoding so login
//     flows, direct-link flows, and token-refresh flows all use the
//     same code path. Drift between callsites caused real M2 bugs in
//     backend testing — we don't want to repeat that pattern here.
// ─────────────────────────────────────────────────────────────────

let objeler = {};

/**
 * Decodes a JWT and returns its payload claims as a plain object.
 *
 * Why custom and not jwt-decode? JWT payloads are just base64url-encoded
 * JSON — five lines, zero dependencies. Adding a package for this is overkill.
 *
 * Why base64url and not base64? JWTs use the URL-safe variant (replaces
 * '+' with '-' and '/' with '_', strips '=' padding). atob() needs the
 * standard form, so we normalize before decoding.
 *
 * Returns an empty object on any error — callers should treat a missing
 * "accountId" claim the same way regardless of cause (malformed token,
 * legacy token, network error during decode).
 */
export const decodeJwt = (token) => {
    if (!token || typeof token !== 'string') return {};
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return {};

        // Base64url → base64. atob() lives at the window scope and only
        // accepts standard base64 with padding restored.
        let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padLen = payload.length % 4;
        if (padLen) payload += '='.repeat(4 - padLen);

        const json = decodeURIComponent(
            atob(payload)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch (err) {
        console.warn('decodeJwt failed:', err);
        return {};
    }
};

/**
 * Reads M2 multi-tenant claims out of an access token and attaches them
 * to objeler.User. Returns true if the token had an accountId claim
 * (i.e. the user has a fresh M2 token), false otherwise.
 *
 * Caller pattern — after a successful login or token refresh:
 *   objeler.User = response.data;
 *   applyAccountContext(response.data?.Token?.AccessToken);
 *
 * If applyAccountContext returns false, the user is on a pre-M2 token
 * and account-aware UI features (My/Team/All tabs, Share-with-Team)
 * should fall back gracefully — these features check for AccountId
 * before rendering.
 */
export const applyAccountContext = (accessToken) => {
    if (!objeler.User) objeler.User = {};
    const claims = decodeJwt(accessToken);

    // Backend's TokenService writes these short claim names directly,
    // and Startup.cs disables ASP.NET's auto-mapping to long URIs.
    // If at any point that mapping comes back, the test in WDCController
    // also reads ClaimTypes.Role as a fallback — we don't need to repeat
    // that here because the JWT is the canonical wire format.
    objeler.User.AccountId = claims.accountId || null;
    objeler.User.AccountName = claims.accountName || null;
    objeler.User.Role = claims.role || null;
    objeler.User.Email = objeler.User.Email || claims.mail || null;

    return !!claims.accountId;
};

/**
 * Returns a snapshot of the current account context, suitable for
 * UI components that need to display or gate on it.
 *
 * Callers should treat a null accountId as "no multi-tenant context"
 * and either render legacy single-user UI or prompt re-login.
 */
export const getCurrentAccount = () => ({
    accountId: objeler.User?.AccountId || null,
    accountName: objeler.User?.AccountName || null,
    role: objeler.User?.Role || null,
    email: objeler.User?.Email || null,
});

/**
 * Convenience predicate. UI code reads more naturally as:
 *   if (hasAccountContext()) { ... show My/Team tabs ... }
 * than as:
 *   if (objeler.User?.AccountId) { ... }
 */
export const hasAccountContext = () => !!objeler.User?.AccountId;

/**
 * Role checks for UI permission gating. The actual authorization happens
 * server-side in DocPermissionService — these helpers just decide which
 * buttons to render. A user who manually pokes a hidden button gets a
 * 403/404 from the backend either way.
 */
export const isAccountAdmin = () => objeler.User?.Role === 'admin';
export const canEditAccountDocs = () => {
    const role = objeler.User?.Role;
    return role === 'admin' || role === 'member';
};

export default objeler;